import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TruckIcon, CarIcon, BikeIcon, MonitorIcon, CheckIcon, PackageIcon } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
type TabId = 'flotillas' | 'particular' | 'carga' | 'motos' | 'monitoreo';
interface TabData {
  id: TabId;
  icon: React.ReactNode;
  title: string;
  features: string[];
  cta: string;
  ctaLink: string;
}
const tabs: TabData[] = [{
  id: 'flotillas',
  icon: <TruckIcon className="w-6 h-6" data-id="element-463" />,
  title: 'Flotillas Empresariales',
  features: ['Geocercas personalizadas para control de rutas', 'Control y monitoreo de combustible', 'Reportes detallados de conducción', 'Alertas de mantenimiento preventivo', 'Múltiples usuarios con permisos', 'API para integración con sistemas', 'Plataforma marca blanca disponible'],
  cta: 'Solicitar Demo Empresarial',
  ctaLink: '#contacto'
}, {
  id: 'particular',
  icon: <CarIcon className="w-6 h-6" data-id="element-464" />,
  title: 'Auto Particular',
  features: ['Localización GPS en tiempo real', 'Bloqueo remoto desde la app', 'Historial de rutas de 90 días', 'Alertas de zona (geocerca)', 'App disponible iOS y Android', 'Compartir ubicación con familia'],
  cta: 'Ver Plan Básico',
  ctaLink: '#planes'
}, {
  id: 'carga',
  icon: <PackageIcon className="w-6 h-6" data-id="element-465" />,
  title: 'Transporte de Carga',
  features: ['Sensor de temperatura para carga refrigerada', 'Monitoreo de combustible anti-robo', 'Dashcam con transmisión en vivo', 'Alertas de fatiga DMS', 'Geocercas de ruta obligatoria', 'Reportes de paradas y tiempos'],
  cta: 'Solución para Transporte',
  ctaLink: '#contacto'
}, {
  id: 'motos',
  icon: <BikeIcon className="w-6 h-6" data-id="element-466" />,
  title: 'Motocicletas',
  features: ['GPS ultracompacto y discreto', 'Bloqueo remoto del motor', 'Alertas de movimiento no autorizado', 'Historial completo de rutas', 'App móvil intuitiva', 'Instalación discreta profesional'],
  cta: 'Ver Plan Moto',
  ctaLink: '#planes'
}, {
  id: 'monitoreo',
  icon: <MonitorIcon className="w-6 h-6" data-id="element-467" />,
  title: 'Central de Monitoreo 24/7',
  features: ['Monitoreo pasivo y activo', 'Alertas de pánico inmediatas', 'Respuesta coordinada ante robo', 'Coordinación con autoridades', 'Notificaciones en tiempo real', 'Personal capacitado 24/7'],
  cta: 'Conocer el Servicio',
  ctaLink: '#contacto'
}];
export function SolutionsSection() {
  const [activeTab, setActiveTab] = useState<TabId>('flotillas');
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  const activeData = tabs.find(t => t.id === activeTab)!;
  return <section id="soluciones" ref={ref} className="w-full bg-brand-bg py-20 md:py-28" data-id="element-468">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-469">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-470">
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4" data-id="element-471">
            Soluciones diseñadas para cada necesidad
          </h2>
          <p className="font-body text-lg text-brand-text-secondary max-w-2xl mx-auto" data-id="element-472">
            Selecciona tu segmento para conocer la solución ideal
          </p>
        </motion.div>

        {/* Tab Selector */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="flex overflow-x-auto pb-4 mb-8 gap-3 md:gap-4 scrollbar-thin" data-id="element-473">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-shrink-0 flex flex-col items-center gap-3 px-6 py-4 rounded-xl border transition-all duration-300 min-w-[140px] ${activeTab === tab.id ? 'bg-brand-accent/10 border-brand-accent text-brand-accent shadow-lg shadow-brand-accent/10' : 'bg-brand-surface/50 border-cyan-subtle text-brand-text-secondary hover:border-brand-accent/50 hover:text-white'}`} data-id="element-474">
              <div className={activeTab === tab.id ? 'text-brand-accent' : ''} data-id="element-475">
                {tab.icon}
              </div>
              <span className="font-subheading text-sm font-medium text-center whitespace-nowrap" data-id="element-476">
                {tab.title}
              </span>
              {activeTab === tab.id && <motion.div layoutId="activeIndicator" className="w-12 h-1 bg-brand-accent rounded-full" data-id="element-477" />}
            </button>)}
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait" data-id="element-478">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} className="glass rounded-2xl p-6 md:p-8" data-id="element-479">
            <div className="grid lg:grid-cols-2 gap-8 items-center" data-id="element-480">
              {/* Features List */}
              <div data-id="element-481">
                <h3 className="font-heading font-bold text-2xl text-white mb-6" data-id="element-482">
                  {activeData.title}
                </h3>
                <ul className="space-y-4 mb-8" data-id="element-483">
                  {activeData.features.map((feature, index) => <motion.li key={feature} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.1
                }} className="flex items-start gap-3" data-id="element-484">
                      <div className="w-5 h-5 rounded-full bg-brand-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-485">
                        <CheckIcon className="w-3 h-3 text-brand-accent" data-id="element-486" />
                      </div>
                      <span className="font-body text-brand-text-secondary" data-id="element-487">
                        {feature}
                      </span>
                    </motion.li>)}
                </ul>
                <a href={activeData.ctaLink} className="inline-flex items-center justify-center bg-brand-accent text-brand-bg font-subheading font-semibold px-6 py-3 rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:shadow-lg hover:shadow-brand-accent/25" data-id="element-488">
                  {activeData.cta}
                </a>
              </div>

              {/* Mockup */}
              <div className="relative" data-id="element-489">
                <TabMockup tabId={activeTab} data-id="element-490" />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}
function TabMockup({
  tabId
}: {
  tabId: TabId;
}) {
  switch (tabId) {
    case 'flotillas':
      return <FleetDashboardMockup data-id="element-491" />;
    case 'particular':
      return <MobileAppMockup data-id="element-492" />;
    case 'carga':
      return <DashcamMockup data-id="element-493" />;
    case 'motos':
      return <MotoAppMockup data-id="element-494" />;
    case 'monitoreo':
      return <MonitoringCenterMockup data-id="element-495" />;
    default:
      return null;
  }
}
function FleetDashboardMockup() {
  return <div className="bg-brand-bg rounded-xl p-4 border border-cyan-subtle" data-id="element-496">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-497">
        <span className="font-subheading text-sm text-white" data-id="element-498">
          Panel de Flotilla
        </span>
        <span className="font-mono text-xs text-brand-accent" data-id="element-499">12 unidades</span>
      </div>

      {/* Map Area */}
      <div className="bg-brand-surface/50 rounded-lg p-3 mb-4 min-h-[200px] relative overflow-hidden" data-id="element-500">
        <div className="absolute inset-0 bg-grid opacity-20" data-id="element-501" />

        {/* Vehicle Markers */}
        {[{
        top: '20%',
        left: '30%',
        active: true
      }, {
        top: '40%',
        left: '60%',
        active: true
      }, {
        top: '60%',
        left: '25%',
        active: false
      }, {
        top: '35%',
        left: '75%',
        active: true
      }, {
        top: '70%',
        left: '50%',
        active: false
      }, {
        top: '25%',
        left: '50%',
        active: true
      }].map((marker, i) => <div key={i} className="absolute" style={{
        top: marker.top,
        left: marker.left
      }} data-id="element-502">
            <div className={`w-3 h-3 rounded-full ${marker.active ? 'bg-brand-accent' : 'bg-brand-text-secondary'}`} data-id="element-503">
              <div className="w-1.5 h-1.5 rounded-full bg-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-504" />
            </div>
          </div>)}

        {/* Routes */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-505">
          <path d="M60,40 Q100,80 150,60 T220,100" fill="none" stroke="#00c8ff" strokeWidth="1.5" strokeOpacity="0.4" strokeDasharray="4 2" data-id="element-506" />
        </svg>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-2" data-id="element-507">
        {[{
        label: 'Activos',
        value: '12'
      }, {
        label: 'Movimiento',
        value: '8'
      }, {
        label: 'Parados',
        value: '4'
      }, {
        label: 'Alertas',
        value: '1'
      }].map(stat => <div key={stat.label} className="text-center p-2 bg-brand-surface/30 rounded" data-id="element-508">
            <p className="font-heading text-lg text-white" data-id="element-509">{stat.value}</p>
            <p className="font-body text-[10px] text-brand-text-secondary" data-id="element-510">
              {stat.label}
            </p>
          </div>)}
      </div>
    </div>;
}
function MobileAppMockup() {
  return <div className="flex justify-center" data-id="element-511">
      <div className="w-[200px] bg-brand-bg rounded-[2rem] p-2 border-4 border-brand-surface" data-id="element-512">
        {/* Phone Screen */}
        <div className="bg-brand-surface rounded-[1.5rem] overflow-hidden" data-id="element-513">
          {/* Status Bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-brand-primary" data-id="element-514">
            <span className="font-mono text-[10px] text-white" data-id="element-515">9:41</span>
            <div className="flex gap-1" data-id="element-516">
              <div className="w-3 h-2 bg-white rounded-sm" data-id="element-517" />
            </div>
          </div>

          {/* App Header */}
          <div className="px-4 py-3 bg-brand-primary border-b border-cyan-subtle" data-id="element-518">
            <span className="font-subheading text-sm text-white" data-id="element-519">
              Mi Vehículo
            </span>
          </div>

          {/* Map */}
          <div className="h-32 bg-brand-bg relative" data-id="element-520">
            <div className="absolute inset-0 bg-grid opacity-30" data-id="element-521" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-522">
              <div className="w-4 h-4 rounded-full bg-brand-accent animate-ping absolute" data-id="element-523" />
              <div className="w-4 h-4 rounded-full bg-brand-accent relative flex items-center justify-center" data-id="element-524">
                <div className="w-2 h-2 rounded-full bg-white" data-id="element-525" />
              </div>
            </div>
          </div>

          {/* Vehicle Card */}
          <div className="p-3" data-id="element-526">
            <div className="bg-brand-bg rounded-lg p-3 border border-cyan-subtle" data-id="element-527">
              <div className="flex items-center gap-2 mb-2" data-id="element-528">
                <div className="w-2 h-2 rounded-full bg-brand-accent" data-id="element-529" />
                <span className="font-subheading text-xs text-white" data-id="element-530">
                  Honda Civic 2022
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]" data-id="element-531">
                <div data-id="element-532">
                  <p className="text-brand-text-secondary" data-id="element-533">Velocidad</p>
                  <p className="font-mono text-brand-accent" data-id="element-534">0 km/h</p>
                </div>
                <div data-id="element-535">
                  <p className="text-brand-text-secondary" data-id="element-536">Estado</p>
                  <p className="text-white" data-id="element-537">Estacionado</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="p-3 pt-0" data-id="element-538">
            <button className="w-full bg-brand-accent text-brand-bg font-subheading text-xs font-semibold py-2 rounded-lg" data-id="element-539">
              Bloquear Motor
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function DashcamMockup() {
  return <div className="space-y-4" data-id="element-540">
      {/* Dashcam Feed */}
      <div className="grid grid-cols-2 gap-3" data-id="element-541">
        {/* Front Camera */}
        <div className="bg-brand-bg rounded-lg p-2 border border-cyan-subtle" data-id="element-542">
          <div className="flex items-center justify-between mb-2" data-id="element-543">
            <span className="font-mono text-[10px] text-brand-text-secondary" data-id="element-544">
              CAM FRONTAL
            </span>
            <div className="flex items-center gap-1" data-id="element-545">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-blink" data-id="element-546" />
              <span className="font-mono text-[10px] text-red-400" data-id="element-547">REC</span>
            </div>
          </div>
          <div className="aspect-video bg-brand-surface rounded relative overflow-hidden" data-id="element-548">
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-549">
              <div className="w-16 h-8 border-2 border-brand-accent/30 rounded" data-id="element-550" />
            </div>
            <div className="absolute bottom-1 left-1 font-mono text-[8px] text-brand-accent/60" data-id="element-551">
              2025-01-15 14:32:05
            </div>
          </div>
        </div>

        {/* DMS Camera */}
        <div className="bg-brand-bg rounded-lg p-2 border border-cyan-subtle" data-id="element-552">
          <div className="flex items-center justify-between mb-2" data-id="element-553">
            <span className="font-mono text-[10px] text-brand-text-secondary" data-id="element-554">
              CAM DMS
            </span>
            <span className="font-mono text-[10px] text-brand-accent" data-id="element-555">
              ACTIVO
            </span>
          </div>
          <div className="aspect-video bg-brand-surface rounded relative overflow-hidden" data-id="element-556">
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-557">
              <div className="w-8 h-10 border-2 border-brand-accent/30 rounded-full" data-id="element-558" />
            </div>
            <div className="absolute top-1 right-1 px-1 py-0.5 bg-brand-accent/20 rounded text-[8px] text-brand-accent" data-id="element-559">
              ALERTA: OFF
            </div>
          </div>
        </div>
      </div>

      {/* Route Map */}
      <div className="bg-brand-bg rounded-lg p-3 border border-cyan-subtle" data-id="element-560">
        <div className="flex items-center justify-between mb-2" data-id="element-561">
          <span className="font-subheading text-sm text-white" data-id="element-562">
            Ruta Activa
          </span>
          <span className="font-mono text-xs text-brand-accent" data-id="element-563">234 km</span>
        </div>
        <div className="h-20 bg-brand-surface/50 rounded relative overflow-hidden" data-id="element-564">
          <div className="absolute inset-0 bg-grid opacity-20" data-id="element-565" />
          <svg className="absolute inset-0 w-full h-full" data-id="element-566">
            <path d="M20,60 Q60,20 120,40 T200,30" fill="none" stroke="#00c8ff" strokeWidth="2" data-id="element-567" />
            <circle cx="20" cy="60" r="4" fill="#00c8ff" data-id="element-568" />
            <circle cx="200" cy="30" r="4" fill="#00c8ff" opacity="0.5" data-id="element-569" />
          </svg>
        </div>
      </div>
    </div>;
}
function MotoAppMockup() {
  return <div className="flex justify-center" data-id="element-570">
      <div className="w-[200px] bg-brand-bg rounded-[2rem] p-2 border-4 border-brand-surface" data-id="element-571">
        <div className="bg-brand-surface rounded-[1.5rem] overflow-hidden" data-id="element-572">
          {/* Status Bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-brand-primary" data-id="element-573">
            <span className="font-mono text-[10px] text-white" data-id="element-574">9:41</span>
            <div className="flex gap-1" data-id="element-575">
              <div className="w-3 h-2 bg-white rounded-sm" data-id="element-576" />
            </div>
          </div>

          {/* App Header */}
          <div className="px-4 py-3 bg-brand-primary border-b border-cyan-subtle" data-id="element-577">
            <span className="font-subheading text-sm text-white" data-id="element-578">Mi Moto</span>
          </div>

          {/* Map */}
          <div className="h-32 bg-brand-bg relative" data-id="element-579">
            <div className="absolute inset-0 bg-grid opacity-30" data-id="element-580" />
            {/* Moto Icon */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-581">
              <BikeIcon className="w-6 h-6 text-brand-accent" data-id="element-582" />
            </div>
            {/* Route */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-583">
              <path d="M30,80 Q60,40 100,60" fill="none" stroke="#00c8ff" strokeWidth="1.5" strokeOpacity="0.5" strokeDasharray="4 2" data-id="element-584" />
            </svg>
          </div>

          {/* Status Card */}
          <div className="p-3" data-id="element-585">
            <div className="bg-brand-bg rounded-lg p-3 border border-cyan-subtle" data-id="element-586">
              <div className="flex items-center gap-2 mb-2" data-id="element-587">
                <div className="w-2 h-2 rounded-full bg-brand-text-secondary" data-id="element-588" />
                <span className="font-subheading text-xs text-white" data-id="element-589">
                  Yamaha MT-07
                </span>
              </div>
              <p className="font-mono text-[10px] text-brand-text-secondary mb-2" data-id="element-590">
                Última ubicación: hace 5 min
              </p>
              <div className="flex gap-2" data-id="element-591">
                <button className="flex-1 bg-brand-accent text-brand-bg font-subheading text-[10px] font-semibold py-1.5 rounded" data-id="element-592">
                  Bloquear
                </button>
                <button className="flex-1 bg-brand-surface text-white font-subheading text-[10px] py-1.5 rounded border border-cyan-subtle" data-id="element-593">
                  Historial
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function MonitoringCenterMockup() {
  return <div className="bg-brand-bg rounded-xl p-4 border border-cyan-subtle" data-id="element-594">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-595">
        <div className="flex items-center gap-2" data-id="element-596">
          <div className="w-2 h-2 rounded-full bg-brand-accent animate-pulse" data-id="element-597" />
          <span className="font-subheading text-sm text-white" data-id="element-598">
            Central de Monitoreo
          </span>
        </div>
        <span className="font-mono text-xs text-brand-accent" data-id="element-599">24/7 ACTIVO</span>
      </div>

      {/* Monitoring Screens Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-600">
        {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="aspect-video bg-brand-surface rounded relative overflow-hidden" data-id="element-601">
            <div className="absolute inset-0 bg-grid opacity-20" data-id="element-602" />
            <div className="absolute top-1 left-1 font-mono text-[8px] text-brand-accent/60" data-id="element-603">
              CAM-{String(i).padStart(2, '0')}
            </div>
            {i <= 3 && <div className="absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full bg-brand-accent" data-id="element-604" />}
          </div>)}
      </div>

      {/* Alert Panel */}
      <div className="bg-brand-surface/50 rounded-lg p-3" data-id="element-605">
        <div className="flex items-center justify-between mb-2" data-id="element-606">
          <span className="font-subheading text-xs text-white" data-id="element-607">
            Alertas Recientes
          </span>
          <span className="font-mono text-[10px] text-brand-text-secondary" data-id="element-608">
            Hoy
          </span>
        </div>
        <div className="space-y-2" data-id="element-609">
          <div className="flex items-center gap-2 text-[10px]" data-id="element-610">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-accent" data-id="element-611" />
            <span className="text-brand-text-secondary" data-id="element-612">
              Unidad 05 - Exceso velocidad
            </span>
            <span className="font-mono text-brand-accent ml-auto" data-id="element-613">14:32</span>
          </div>
          <div className="flex items-center gap-2 text-[10px]" data-id="element-614">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-text-secondary" data-id="element-615" />
            <span className="text-brand-text-secondary" data-id="element-616">
              Unidad 12 - Geocerca salida
            </span>
            <span className="font-mono text-brand-accent ml-auto" data-id="element-617">13:45</span>
          </div>
        </div>
      </div>
    </div>;
}